import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { DocumentTextIcon } from '@heroicons/react/24/outline'
import { HomeworkApi, HomeworkSubmission } from '../../services/homeworkApi'
import AdminStudentCaseStudyModal, {
  CaseStudyViewTarget,
} from '../../components/homework/AdminStudentCaseStudyModal'

const STATUS_LABEL: Record<string, string> = {
  submitted: 'Đã nộp',
  in_review: 'Đang chấm',
  completed: 'Hoàn thành',
  returned: 'Trả bài',
  draft: 'Nháp',
}

export default function AdminHomeworkSubmissionsPage() {
  const { id: courseId, homeworkId } = useParams()
  const [rows, setRows] = useState<HomeworkSubmission[]>([])
  const [title, setTitle] = useState('BTVN')
  const [loading, setLoading] = useState(true)
  const [caseTarget, setCaseTarget] = useState<CaseStudyViewTarget>(null)

  useEffect(() => {
    const load = async () => {
      if (!homeworkId) return
      try {
        setLoading(true)
        const hw = await HomeworkApi.get(Number(homeworkId))
        setTitle(hw.name)
        const data = await HomeworkApi.listSubmissions(Number(homeworkId))
        setRows(data)
      } catch (e: any) {
        toast.error(e?.message || 'Không tải được bài làm')
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [homeworkId])

  return (
    <div className="p-4 lg:p-8 max-w-6xl mx-auto">
      <div className="mb-4">
        <Link to={`/admin/courses/${courseId}`} className="text-sm text-m2-primary hover:underline">
          ← Quay lại khóa học
        </Link>
        <h1 className="text-2xl font-bold mt-2">Quản lý bài làm — {title}</h1>
      </div>

      {loading ? (
        <p className="text-m2-on-surface-variant">Đang tải...</p>
      ) : rows.length === 0 ? (
        <p className="text-m2-on-surface-variant">Chưa có học viên nộp bài.</p>
      ) : (
        <div className="overflow-x-auto border rounded-xl">
          <table className="min-w-full text-sm">
            <thead className="bg-m2-surface-high text-left">
              <tr>
                <th className="px-3 py-2">Học viên</th>
                <th className="px-3 py-2">Lớp</th>
                <th className="px-3 py-2">Trạng thái</th>
                <th className="px-3 py-2">AI scan</th>
                <th className="px-3 py-2">Comment</th>
                <th className="px-3 py-2">Nộp lúc</th>
                <th className="px-3 py-2">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.studentName || `#${r.userId}`}</div>
                    <div className="text-xs text-m2-on-surface-variant">{r.studentEmail}</div>
                  </td>
                  <td className="px-3 py-2">{r.classroomName || r.classroomId}</td>
                  <td className="px-3 py-2">{STATUS_LABEL[r.status] || r.status}</td>
                  <td className="px-3 py-2">{r.aiScanStatus}</td>
                  <td className="px-3 py-2">
                    {r.commentCount || 0}
                    {r.pendingAiCount ? (
                      <span className="ml-1 text-violet-600">({r.pendingAiCount} chờ duyệt)</span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">
                    {r.submittedAt ? new Date(r.submittedAt).toLocaleString('vi-VN') : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      {r.caseStudyId ? (
                        <button
                          type="button"
                          title={r.caseStudyName ? `Case study: ${r.caseStudyName}` : 'Xem case study'}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
                          onClick={() =>
                            setCaseTarget({
                              userId: Number(r.userId),
                              courseId: Number(r.courseId || courseId),
                              studentName: r.studentName,
                            })
                          }
                        >
                          <DocumentTextIcon className="h-4 w-4" />
                        </button>
                      ) : null}
                      <Link
                        to={`/admin/courses/${courseId}/homeworks/${homeworkId}/submissions/${r.id}`}
                        className="text-m2-primary hover:underline whitespace-nowrap"
                      >
                        Xem / chấm
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AdminStudentCaseStudyModal target={caseTarget} onClose={() => setCaseTarget(null)} />
    </div>
  )
}
